// Forwards clicks on the label next to <shopify-account> into the component itself, so the
// text reads as part of the same control. Delegated from the document because account buttons
// render in several places (header, menu drawer) and get re-rendered by the theme editor.
document.addEventListener('click', (event) => {
  const wrapper = event.target.closest('.account-button');
  if (!wrapper) return;

  const account = wrapper.querySelector('shopify-account');
  // Clicks inside the component retarget to the host, so this also stops us re-entering
  // on the synthetic click dispatched below.
  if (!account || account.contains(event.target)) return;

  event.preventDefault();

  // The real trigger lives in the component's shadow root; clicking the host alone is ignored.
  const trigger = account.shadowRoot?.querySelector('button, a, [role="button"]') ?? account;
  trigger.click();
});

// The account sheet's contents are server-rendered inside the component's shadow root, so theme
// CSS can't reach them and very few of the documented --shopify-account-* variables paint them either.
// Adopting a sheet into the (open) shadow root is the only way in. Custom properties still inherit
// across the shadow boundary from the host, so these rules read the theme's colour scheme variables
// directly. Content in the shop-login slot belongs to shop-js and keeps its own shadow root, so it
// stays out of reach of everything below.
//
// This mirrors .btn and its variants from theme.css rather than reusing them. The real button wipes
// a ::before overlay across itself on hover, which depends on a .btn__text wrapper to keep the label
// painting above the overlay. The menu links are bare text, so the wipe is done here with an
// animated background-image, which always paints beneath text and needs no extra element. The
// direction is flipped for RTL because background-position has no logical equivalent.
//
// Everything keys off attributes the theme sets on the host, so the Shopify default styling is left
// untouched unless a merchant opts in. --buttons-padding is only ever declared on .btn in theme.css,
// never at :root, so it can't be inherited in here and each variant restates it.
const ACCOUNT_SHEET_CSS = `
  :host([data-menu-link-style]) .account__menu-region a {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0 var(--buttons-gap, 0.4rem);
    height: var(--buttons-height);
    padding: 0 var(--ce-link-padding);
    color: var(--ce-link-text);
    background-color: var(--ce-link-background);
    background-image: linear-gradient(var(--ce-link-wipe), var(--ce-link-wipe));
    background-repeat: no-repeat;
    background-origin: border-box;
    background-position: left center;
    background-size: 0% 100%;
    border: var(--buttons-border-width) solid var(--ce-link-border);
    border-radius: var(--buttons-radius, 0);
    font-family: var(--font-button-family);
    font-weight: var(--font-button-weight);
    font-size: 100%;
    line-height: 30px;
    letter-spacing: var(--buttons-letter-spacing, 0);
    text-transform: var(--buttons-transform, capitalize);
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    transition: background-size var(--animation-button, 0.4s ease),
      color var(--animation-button, 0.4s ease);
  }

  :host([data-menu-link-style="primary"]) .account__menu-region a {
    --ce-link-background: rgb(var(--color-button));
    --ce-link-text: rgb(var(--color-button-text));
    --ce-link-border: rgb(var(--color-button));
    --ce-link-wipe: rgb(var(--color-button-text));
    --ce-link-hover-text: rgb(var(--color-button));
    --ce-link-padding: 3.2rem;
  }

  :host([data-menu-link-style="secondary"]) .account__menu-region a {
    --ce-link-background: rgb(var(--color-secondary-button));
    --ce-link-text: rgb(var(--color-secondary-button-text));
    --ce-link-border: rgb(var(--color-secondary-button-border));
    --ce-link-wipe: rgb(var(--color-button));
    --ce-link-hover-text: rgb(var(--color-button-text));
    --ce-link-padding: 3.2rem;
  }

  :host([data-menu-link-style="outline"]) .account__menu-region a {
    --ce-link-background: transparent;
    --ce-link-text: rgb(var(--color-text-heading));
    --ce-link-border: rgb(var(--color-text-heading));
    --ce-link-wipe: rgb(var(--color-button));
    --ce-link-hover-text: rgb(var(--color-button-text));
    --ce-link-padding: 1.6rem;
  }

  :host([data-menu-link-full-width]) .account__menu-region a {
    width: 100%;
  }

  :host([data-menu-link-style]) .account__menu-region a:dir(rtl) {
    background-position: right center;
  }

  @media (hover: hover) and (pointer: fine) {
    :host([data-menu-link-style]) .account__menu-region a:hover {
      background-size: 100% 100%;
      color: var(--ce-link-hover-text);
    }
  }
`;

const styledAccounts = new WeakSet();
let accountSheet;

function adoptAccountSheetStyles(account) {
  const root = account.shadowRoot;
  if (!root || styledAccounts.has(account)) return;

  if (!accountSheet) {
    accountSheet = new CSSStyleSheet();
    accountSheet.replaceSync(ACCOUNT_SHEET_CSS);
  }

  /* Appended last so it outweighs the component's own sheets 
  instead of replacing them. 

  Adopted sheets apply to the whole tree, 
  so the server-rendered menu is covered whenever it arrives. */

  root.adoptedStyleSheets = [...root.adoptedStyleSheets, accountSheet];
  styledAccounts.add(account);
}

if ('adoptedStyleSheets' in Document.prototype) {
  customElements.whenDefined('shopify-account').then(() => {
    document.querySelectorAll('shopify-account').forEach(adoptAccountSheetStyles);

    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          if (node.matches('shopify-account')) adoptAccountSheetStyles(node);
          else node.querySelectorAll('shopify-account').forEach(adoptAccountSheetStyles);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  });
}
